import { useTranslation } from 'react-i18next';
import useRequest from '../../hooks/use-request.hook';
import { useEffect, useState } from 'react';
import Aside from '../../components/layout/asaid.component';
import Header from '../../components/layout/header.component';
import Footer from '../../components/layout/footer.component';
import Datatable from '../../components/datatable/datatable.component';
import { UsersService } from '../../services/users.service';
import { PROFILES } from '../../constants';
import { AlertService } from '../../services/alert.service';
import ActionsDropdown from '../../components/datatable/actions-dropdown.component';
import Icon from '../../components/forms/icon.component';
import LogData from '../../components/logs/log-data.component';
import PrincipalCard from '../../components/cards/principal-card.component';
import DateRegister from '../../components/datatable/register-data.component';
import { useSession } from '../../hooks/use-session.hook';
import useBgPages from '../../hooks/use-bg-pages.hook';
import AddUser from './modal/add-user.modal';
import InputCopyClipboard from '../../components/forms/clipboard-copy.component';

function UsersPage() {
  useBgPages();
  const { session, selectedAccount } = useSession();
  const { t } = useTranslation();
  const { call, isRequesting } = useRequest();
  const [rows, setRows] = useState([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [activeUsers, setActiveUsers] = useState<number>(0);

  const dataStatistics = [
    { label: 'Usuarios', total: totalUsers, type: '', icon: 'ki-book-open' },
    { label: 'Activos', total: activeUsers, type: 'primary', icon: 'ki-check-circle' }
  ];

  const columns = [
    {
      name: t('users-page.title-column-1'),
      sortable: true,
      selector: (r: any) => `${r.full_name} ${r.username} ${r.email}`,

      width: 1,
      cell: (row: any) => {
        return (
          <ul className="list-group-item">
            <li className="list-group-item">
              <div className="d-flex flex-column">
                <div className="mb-1">
                  <strong className="fs-6">{t('users-page.title-username-1')}:</strong>
                  <span className="ms-1">{row.username || t('general.no-info')}</span>
                </div>
                <div className="mb-1">
                  <strong className="fs-6 d-block">{t('users-page.title-name-2')}:</strong>
                  <span className="ms-1">{row.full_name || t('general.no-info')}</span>
                </div>
                <div className="mb-1">
                  <strong className="fs-6 d-block">{t('general.mail')}:</strong>
                  <span className="ms-1">{row.email || t('general.no-info')}</span>
                </div>
              </div>
            </li>
          </ul>
        );
      }
    },
    {
      name: t('users-page.label-profile'),
      sortable: true,
      selector: (r: any) => `${r.profile} ${r.status}`,
      width: 10,
      cell: (row: any) => (
        <div className="text-nowrap mb-2 fs-5">
          <div className="w-100 mb-1">
            <strong className="fs-6">{t('general.profile')}:</strong>
            <span className="ms-1">
              {(() => {
                switch (row.profile) {
                  case PROFILES.ROOT:
                    return t('users-page.root-profile');
                  case PROFILES.ADMIN:
                    return t('users-page.admin-profile');
                  case PROFILES.STANDARD_USER:
                    return t('users-page.standard-user-profile');
                  case PROFILES.AGENTE:
                    return t('users-page.agent-profile');
                  default:
                    return t('general.no-info');
                }
              })()}
            </span>
          </div>
          <div className="w-100 mb-1">
            <strong className="fs-6">Status:</strong>
            {row.status ? (
              <span className="badge text-primary fw-bolder fs-6">{t('general.active')}</span>
            ) : (
              <span className="badge text-danger fw-bolder fs-6">{t('general.inactive')}</span>
            )}
          </div>
        </div>
      )
    },
    {
      name: t('general.rol'),
      sortable: true,
      selector: (r: any) => r.accounts?.[0]?.role?.name,
      width: 10,
      cell: (row: any) => {
        const roleName = row.accounts?.[0]?.role?.name || row.role?.name;

        return (
          <div className="fs-5">
            {roleName ? (
              <div className="text-nowrap">
                <span className="text-info fw-bolder fs-6">{roleName}</span>
              </div>
            ) : (
              <span className="text-gray-700 badge bg-secondary fw-bolder fs-6">
                <Icon icon="ki-information" size="fs-2" className="me-1 text-danger fw-bolder" />
                {row.profile === 'ADMIN' ? t('general.no-apply') : t('general.no-info')}
              </span>
            )}
          </div>
        );
      }
    },
    ...(session.profile !== PROFILES.ROOT
      ? []
      : [
          {
            name: t('users-page.label-comany'),
            sortable: true,
            selector: (r: any) => r.accounts[0]?.account?.company,
            width: 20,
            cell: (row: any) => {
              return (
                <div className="fs-5">
                  {row.accounts?.length ? (
                    <ul className="list-group-item">
                      <li className="list-group-item">
                        <div className="d-flex flex-column">
                          {row.accounts[0]?.account?.company && (
                            <div className="w-100 mb-1">
                              <strong className="fs-6">
                                {t('users-page.modal-add-user.company-label')}:
                              </strong>
                              <span className="ms-1">
                                {row.accounts[0].account.company.name || t('general.no-info')}
                              </span>
                            </div>
                          )}
                          <div className="w-100 mb-1">
                            <strong className="fs-6">{t('users-page.account-label')}:</strong>
                            {row.accounts.map((acc: any, i: number) => (
                              <span
                                className="ms-1 d-block"
                                key={acc.account?.id || acc.account?._id || i}
                              >
                                {acc.account.name || t('general.no-info')}
                              </span>
                            ))}
                          </div>
                        </div>
                      </li>
                    </ul>
                  ) : (
                    <span className="text-gray-700 badge bg-secondary fw-bolder fs-6">
                      <Icon
                        icon="ki-information"
                        size="fs-4"
                        className="me-1 text-danger fw-bolder"
                      />
                      {t('general.no-apply')}
                    </span>
                  )}
                </div>
              );
            }
          }
        ]),
    {
      name: 'Alta',
      sortable: true,
      width: 20,
      selector: (r: any) => `${r.created_at}`,
      cell: (row: any) => {
        return <DateRegister created_at={row.created_at} />;
      }
    },
    {
      name: 'Último acceso',
      sortable: true,
      width: 20,
      selector: (r: any) => `${r.last_login}`,
      cell: (row: any) => {
        return <DateRegister created_at={row.last_login} />;
      }
    },
    {
      name: t('general.acctions'),
      sortable: true,
      center: true,
      width: 10,
      filterColumn: false,
      cell: (rows: any) => {
        const options = [
          {
            label: t('users-page.modal-add-user.recover-password-title'),
            icon: 'ki-key-square',
            color: 'info',
            onClick: () => recoverPassword(rows)
          }
        ];
        return (
          <ActionsDropdown
            edit={() => setShowModal({ show: true, data: rows })}
            changeStatus={() => changeStatus(rows._id, 'changeStatus-7354092736', rows.status)}
            // delete={() => deleteUser(rows._id, 'deleteUser-8763456783', rows.username)}   Habilitar  hasta poder validar logica de negocio.
            rowStatus={rows.status}
            options={options}
          />
        );
      }
    }
  ];

  const recoverPassword = async (data: any) => {
    const isConfirmed = await AlertService.warning(
      t('general.atention'),
      `¿Deseas recuperar la contraseña del usuario?, ${data.username}`,
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;
    try {
      const response = await UsersService.recoverPassworUsers({ user: data._id });

      const html = response.psw ? (
        <div>
          <p>{t('users-page.modal-add-user.password')}</p>
          <div className="width-fit-content m-auto">
            <InputCopyClipboard text={response.psw} />
          </div>
        </div>
      ) : (
        <div>
          <p>La contraseña se generó pero no se ha podido mostrar. Contacta a soporte.</p>
        </div>
      );

      await AlertService.success(<p className="h2">¡Contraseña recuperada correctamente!</p>, html);

      LogData({ user: data._id }, 'recoverPassword82727272666', window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const changeStatus = async (id: string, buttonId: string, status: boolean) => {
    const isConfirmed = await AlertService.warning(
      t('general.atention'),
      t('users-page.change-status-warning'),
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;
    try {
      await UsersService.changeStatus({ user: id }, status ? 'disable' : 'enable');
      AlertService.toastSuccess(t('users-page.change-status-success'));
      await getUsers();
      LogData({ user: id, type: status ? 'disable' : 'enable' }, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const deleteUser = async (id: string, buttonId: string, username: string) => {
    const isConfirmed = await AlertService.warning(
      t('general.atention'),
      `¿Deseas eliminar este usuario?, ${username}`,
      () => Promise.resolve(true)
    );
    if (!isConfirmed) return;
    try {
      await UsersService.deleteUser(id);
      AlertService.toastSuccess(`${t('general.atention')}, el usuario se eliminó correctamente.`);
      await getUsers();
      LogData({ user: id, type: 'delete-user' }, buttonId, window.location.href);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const getUsers = async () => {
    try {
      let data = [];
      if (session.profile === PROFILES.ROOT) {
        data = await call(() => UsersService.getUsers());
      } else if (session.profile === PROFILES.ADMIN) {
        data = await call(() => UsersService.getUsersForAccount(selectedAccount.account_id));
      }

      setRows(data);
      setTotalUsers(data.length);
      const activeUsers = data.filter((user: any) => user.status === true);
      setActiveUsers(activeUsers.length);
    } catch (errorCode: any) {
      console.log('Error', errorCode);
      AlertService.toastError(errorCode.code || errorCode);
    }
  };
  const [showModal, setShowModal] = useState({
    show: false,
    data: null
  });

  const handleShowModal = async (refresh: boolean) => {
    if (refresh) {
      await getUsers();
    }
    setShowModal({ show: false, data: null });
  };

  useEffect(() => {
    getUsers();
  }, []);

  return (
    <>
      <div className="page d-flex flex-row flex-column-fluid">
        <Aside />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <Header />
          <div className="content fs-6 d-flex flex-column flex-column-fluid" id="kt_content">
            <div className="post fs-6 d-flex flex-column-fluid" id="kt_post">
              <div className="container-xxl">
                <PrincipalCard
                  dataStatistics={dataStatistics}
                  btnCreate
                  onClick={() => setShowModal({ show: true, data: null })}
                  btnRefresh
                  onRefresh={async () => {
                    await getUsers();
                    AlertService.toastSuccess('¡ Éxito !, información actualizada.');
                  }}
                  title="Registro"
                  titleBolder={t('asaid.link-users')}
                  subtitle="Registra nuevos usuarios o actualiza su información."
                  breadcrumbItems={['Usuarios']}
                  textButton="Agregar usuario"
                  iconButton="ki-soft-2"
                />
                <div className="card mb-5 mb-xxl-8">
                  <div className="card-header align-items-center border-0 mt-5">
                    <div className="card-title align-items-start flex-column animate-fadeInUp animate-delay-1 h3">
                      <span className="fw-bolder fw-bold fs-2">{t('users-page.title-page')}</span>
                      <span className="text-gray-700 mt-2 fw-semibold fs-6">
                        {t('users-page.registered-text')}
                      </span>
                    </div>
                  </div>
                  <div className="card-body pt-1">
                    <Datatable columns={columns} data={rows} isLoading={isRequesting} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
      <AddUser
        show={showModal.show}
        onRequestClose={(refresh: any) => handleShowModal(refresh)}
        payload={showModal.data}
      />
    </>
  );
}
export default UsersPage;
